import { useState } from "react";
import {
  instructorService,
  Instructor,
} from "../../services/instructorService";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { AlertCircle, Loader2 } from "lucide-react";

interface DeleteInstructorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  instructor: Instructor | null;
}

export function DeleteInstructorDialog({
  isOpen,
  onClose,
  onSuccess,
  instructor,
}: DeleteInstructorDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!instructor) return;
    setIsLoading(true);
    setError(null);

    try {
      await instructorService.deleteInstructor(instructor._id);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Delete instructor error:", err);
      // The backend returns strict validation messages (e.g., if courses exist)
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to delete instructor. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!instructor) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Instructor">
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm border border-red-100">
            {error}
          </div>
        )}

        <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg text-amber-800 border border-amber-100">
          <AlertCircle className="shrink-0 mt-0.5" size={20} />
          <div className="text-sm">
            <p className="font-semibold mb-1">Warning Details</p>
            <p>
              Are you sure you want to delete{" "}
              <span className="font-bold">
                {instructor.user?.firstName} {instructor.user?.lastName}
              </span>
              ? This action cannot be undone.
            </p>
          </div>
        </div>

        <p className="text-sm text-gray-500">
          Note: Instructors with active courses or enrollments cannot be
          deleted. You must transfer or delete their courses first.
        </p>

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white border-transparent focus:ring-red-500"
            onClick={handleDelete}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="animate-spin mr-2" size={16} />}
            Delete Instructor
          </Button>
        </div>
      </div>
    </Modal>
  );
}
